import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Pencil, Plus, X, RefreshCw, LogOut } from "lucide-react";
import Seo from "../components/site/Seo";
import { api, getAdminToken, setAdminToken, extractError } from "../lib/api";
import { WORK_FILTERS, PARTNER_CATEGORIES, CAREERS } from "../lib/content";

const TABS = [
  { id: "projects", label: "Projects" },
  { id: "partners", label: "Partners" },
  { id: "openings", label: "Openings" },
  { id: "enquiries", label: "Enquiries" },
  { id: "applications", label: "Applications" },
  { id: "campaigns", label: "Campaigns" },
  { id: "partner-requests", label: "Partner Requests" },
];

const CATEGORIES = WORK_FILTERS.filter((c) => c !== "ALL");
const PARTNER_CATEGORY_NAMES = PARTNER_CATEGORIES.map((c) => c.title);

const emptyProject = {
  title: "",
  category: "EVENTS",
  client: "",
  year: String(new Date().getFullYear()),
  summary: "",
  description: "",
  scope: "",
  image: "",
  featured: false,
  order: 10,
  is_placeholder: false,
};

const emptyPartner = { name: "", category: PARTNER_CATEGORY_NAMES[0], logo: "", website: "", order: 10, is_placeholder: false };

const emptyOpening = {
  title: "",
  department: CAREERS.opportunities[0],
  location: "Bengaluru",
  type: "Full-time",
  experience: "",
  description: "",
  responsibilities: "",
  is_open: true,
  order: 10,
  is_placeholder: false,
};

const Label = ({ children }) => (
  <label className="mb-2 block text-[10.5px] font-semibold uppercase" style={{ letterSpacing: "0.16em", color: "rgba(23,21,34,0.55)" }}>
    {children}
  </label>
);

const Input = (props) => <input {...props} className="field-input" style={{ height: 44, fontSize: 14 }} />;
const Area = (props) => <textarea {...props} className="field-input" style={{ minHeight: 96, fontSize: 14 }} />;
const Select = ({ options, ...props }) => (
  <select {...props} className="field-input" style={{ height: 44, fontSize: 14 }}>
    {options.map((o) => (
      <option key={o} value={o}>
        {o}
      </option>
    ))}
  </select>
);

const Panel = ({ title, onClose, children }) => (
  <div className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto p-4 sm:p-8" style={{ background: "rgba(14,13,20,0.45)" }}>
    <div
      className="w-full max-w-[720px] rounded-[20px] bg-white p-6 sm:p-8"
      style={{ border: "1px solid rgba(23,21,34,0.12)", boxShadow: "var(--shadow-float-strong)" }}
      data-testid="admin-editor-panel"
    >
      <div className="flex items-center justify-between">
        <h2 className="font-display text-[20px] font-extrabold tracking-[-0.02em]">{title}</h2>
        <button type="button" onClick={onClose} aria-label="Close editor" data-testid="admin-editor-close" className="rounded-full p-2 hover:bg-lavender">
          <X size={18} strokeWidth={1.7} />
        </button>
      </div>
      <div className="mt-7">{children}</div>
    </div>
  </div>
);

const Table = ({ columns, rows, renderRow, emptyText, testId }) => (
  <div className="overflow-x-auto" data-testid={testId}>
    {rows.length === 0 ? (
      <p className="py-14 text-center text-[14px]" style={{ color: "rgba(23,21,34,0.5)" }} data-testid={`${testId}-empty`}>
        {emptyText}
      </p>
    ) : (
      <table className="w-full border-collapse text-left">
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col}
                className="whitespace-nowrap px-4 py-3 text-[10.5px] font-semibold uppercase"
                style={{ letterSpacing: "0.14em", color: "rgba(23,21,34,0.5)", borderBottom: "1px solid rgba(23,21,34,0.14)" }}
              >
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{rows.map(renderRow)}</tbody>
      </table>
    )}
  </div>
);

const Cell = ({ children, nowrap = false, muted = false }) => (
  <td
    className={`px-4 py-4 align-top text-[13.5px] ${nowrap ? "whitespace-nowrap" : ""}`}
    style={{ borderBottom: "1px solid rgba(23,21,34,0.08)", color: muted ? "rgba(23,21,34,0.6)" : "#171522" }}
  >
    {children}
  </td>
);

export default function Admin() {
  const [token, setToken] = useState(getAdminToken());
  const [input, setInput] = useState("");
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(Boolean(getAdminToken()));
  const [tab, setTab] = useState("projects");
  const [counts, setCounts] = useState({});
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [editor, setEditor] = useState(null); // {kind, item}

  const headers = useMemo(() => ({ headers: { "X-Admin-Token": token } }), [token]);

  const verify = useCallback(
    async (candidate) => {
      try {
        const res = await api.get("/admin/session", { headers: { "X-Admin-Token": candidate } });
        setCounts(res.data.counts || {});
        setAuthed(true);
        setToken(candidate);
        setAdminToken(candidate);
        return true;
      } catch (error) {
        setAuthed(false);
        setAdminToken("");
        return false;
      } finally {
        setChecking(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (token) verify(token);
    else setChecking(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadTab = useCallback(
    async (which) => {
      if (!authed) return;
      setLoading(true);
      try {
        let rows = [];
        if (which === "projects") rows = (await api.get("/admin/projects", headers)).data;
        else if (which === "partners") rows = (await api.get("/admin/partners", headers)).data;
        else if (which === "openings") rows = (await api.get("/admin/openings", headers)).data;
        else rows = (await api.get(`/admin/submissions/${which}`, headers)).data;
        setData((prev) => ({ ...prev, [which]: rows }));
        const session = await api.get("/admin/session", headers);
        setCounts(session.data.counts || {});
      } catch (error) {
        toast.error(extractError(error, "Could not load data."));
      } finally {
        setLoading(false);
      }
    },
    [authed, headers],
  );

  useEffect(() => {
    if (authed) loadTab(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed, tab]);

  const onLogin = async (event) => {
    event.preventDefault();
    const ok = await verify(input.trim());
    if (ok) toast.success("Admin unlocked.");
    else toast.error("Invalid admin token.");
  };

  const logout = () => {
    setAdminToken("");
    setToken("");
    setAuthed(false);
    setData({});
  };

  const remove = async (kind, id) => {
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!window.confirm("Delete this entry? This cannot be undone.")) return;
    try {
      if (kind === "projects") await api.delete(`/admin/projects/${id}`, headers);
      else if (kind === "partners") await api.delete(`/admin/partners/${id}`, headers);
      else if (kind === "openings") await api.delete(`/admin/openings/${id}`, headers);
      else await api.delete(`/admin/submissions/${kind}/${id}`, headers);
      toast.success("Deleted.");
      loadTab(kind);
    } catch (error) {
      toast.error(extractError(error, "Could not delete."));
    }
  };

  const saveProject = async (form) => {
    const payload = {
      ...form,
      order: Number(form.order) || 99,
      scope: form.scope ? form.scope.split(",").map((s) => s.trim()).filter(Boolean) : [],
    };
    try {
      if (form.id) await api.put(`/admin/projects/${form.id}`, payload, headers);
      else await api.post("/admin/projects", payload, headers);
      toast.success("Project saved.");
      setEditor(null);
      loadTab("projects");
    } catch (error) {
      toast.error(extractError(error, "Could not save project."));
    }
  };

  const savePartner = async (form) => {
    const payload = { ...form, order: Number(form.order) || 99 };
    try {
      if (form.id) await api.put(`/admin/partners/${form.id}`, payload, headers);
      else await api.post("/admin/partners", payload, headers);
      toast.success("Partner saved.");
      setEditor(null);
      loadTab("partners");
    } catch (error) {
      toast.error(extractError(error, "Could not save partner."));
    }
  };

  const saveOpening = async (form) => {
    const payload = {
      ...form,
      order: Number(form.order) || 99,
      responsibilities: form.responsibilities
        ? form.responsibilities.split("\n").map((s) => s.trim()).filter(Boolean)
        : [],
    };
    try {
      if (form.id) await api.put(`/admin/openings/${form.id}`, payload, headers);
      else await api.post("/admin/openings", payload, headers);
      toast.success("Opening saved.");
      setEditor(null);
      loadTab("openings");
    } catch (error) {
      toast.error(extractError(error, "Could not save opening."));
    }
  };

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ivory">
        <p className="text-[14px]" style={{ color: "rgba(23,21,34,0.6)" }}>
          Checking admin session…
        </p>
      </div>
    );
  }

  if (!authed) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ivory px-6">
        <Seo title="Admin | LYKIT Entertainment" description="LYKIT content console" />
        <form
          onSubmit={onLogin}
          data-testid="admin-login-form"
          className="w-full max-w-[420px] rounded-[20px] bg-white p-8"
          style={{ border: "1px solid rgba(23,21,34,0.12)", boxShadow: "var(--shadow-float)" }}
        >
          <img src="/brand/lykit-mark.png" alt="" aria-hidden="true" className="h-[22px] w-auto" />
          <h1 className="mt-6 font-display text-[24px] font-extrabold tracking-[-0.02em]">Content Console</h1>
          <p className="mt-3 text-[13.5px]" style={{ color: "rgba(23,21,34,0.6)" }}>
            Enter the admin token to manage projects, partners, openings and submissions.
          </p>
          <div className="mt-7">
            <Label>Admin Token</Label>
            <Input
              type="password"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="••••••••"
              data-testid="admin-token-input"
              autoComplete="current-password"
            />
          </div>
          <button type="submit" className="btn btn-primary mt-7 w-full" data-testid="admin-login-button">
            Unlock
          </button>
          <Link to="/" className="mt-6 block text-center text-[13px]" style={{ color: "rgba(23,21,34,0.55)" }}>
            Back to website
          </Link>
        </form>
      </div>
    );
  }

  const rows = data[tab] || [];

  return (
    <div className="min-h-screen bg-ivory">
      <Seo title="Admin | LYKIT Entertainment" description="LYKIT content console" />

      <header className="sticky top-0 z-40" style={{ background: "rgba(247,246,242,0.94)", backdropFilter: "blur(12px)", borderBottom: "1px solid rgba(23,21,34,0.1)" }}>
        <div className="lk-container-wide flex flex-wrap items-center justify-between gap-4 py-4">
          <div className="flex items-center gap-3">
            <img src="/brand/lykit-mark.png" alt="" aria-hidden="true" className="h-[20px] w-auto" />
            <span className="font-display text-[16px] font-extrabold tracking-[-0.01em]">LYKIT Content Console</span>
          </div>
          <div className="flex items-center gap-3">
            <button type="button" onClick={() => loadTab(tab)} className="btn btn-outline" style={{ height: 40, paddingInline: 16, fontSize: 11 }} data-testid="admin-refresh">
              <RefreshCw size={14} strokeWidth={1.8} /> Refresh
            </button>
            <Link to="/" className="btn btn-outline" style={{ height: 40, paddingInline: 16, fontSize: 11 }}>
              View Site
            </Link>
            <button type="button" onClick={logout} className="btn btn-primary" style={{ height: 40, paddingInline: 16, fontSize: 11 }} data-testid="admin-logout">
              <LogOut size={14} strokeWidth={1.8} /> Lock
            </button>
          </div>
        </div>

        <div className="lk-container-wide no-scrollbar flex gap-1 overflow-x-auto pb-3">
          {TABS.map((t) => {
            const active = tab === t.id;
            const count = counts[t.id === "partner-requests" ? "partner_requests" : t.id];
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                data-testid={`admin-tab-${t.id}`}
                className="relative shrink-0 px-4 py-2 text-[12.5px] font-medium"
                style={{ color: active ? "#171522" : "rgba(23,21,34,0.55)" }}
              >
                {t.label}
                {typeof count === "number" && (
                  <span className="ml-2 text-[11px]" style={{ color: "rgba(23,21,34,0.4)" }}>
                    {count}
                  </span>
                )}
                {active && <span aria-hidden="true" className="absolute inset-x-3 bottom-0 h-[2px] bg-gold" />}
              </button>
            );
          })}
        </div>
      </header>

      <main className="lk-container-wide py-10">
        <div className="flex items-center justify-between">
          <h1 className="font-display text-[22px] font-extrabold tracking-[-0.02em]">
            {TABS.find((t) => t.id === tab)?.label}
          </h1>
          {["projects", "partners", "openings"].includes(tab) && (
            <button
              type="button"
              data-testid={`admin-add-${tab}`}
              onClick={() =>
                setEditor({
                  kind: tab,
                  item: tab === "projects" ? { ...emptyProject } : tab === "partners" ? { ...emptyPartner } : { ...emptyOpening },
                })
              }
              className="btn btn-primary"
              style={{ height: 42, paddingInline: 18, fontSize: 11 }}
            >
              <Plus size={14} strokeWidth={2} /> Add {tab === "projects" ? "Project" : tab === "partners" ? "Partner" : "Opening"}
            </button>
          )}
        </div>

        <div className="mt-7 rounded-[18px] bg-white" style={{ border: "1px solid rgba(23,21,34,0.1)" }}>
          {loading && (
            <p className="px-6 py-5 text-[13px]" style={{ color: "rgba(23,21,34,0.5)" }}>
              Loading…
            </p>
          )}

          {tab === "projects" && (
            <Table
              testId="admin-projects-table"
              columns={["Title", "Category", "Client", "Year", "Featured", "Order", ""]}
              rows={rows}
              emptyText="No projects yet. Use “Add Project” to create the first one."
              renderRow={(row) => (
                <tr key={row.id}>
                  <Cell>
                    <span className="font-medium">{row.title}</span>
                    {row.is_placeholder && (
                      <span className="ml-2 text-[10.5px] uppercase" style={{ letterSpacing: "0.12em", color: "#B08A4B" }}>
                        placeholder
                      </span>
                    )}
                    <span className="mt-1 block text-[12px]" style={{ color: "rgba(23,21,34,0.5)" }}>
                      /work/{row.slug}
                    </span>
                  </Cell>
                  <Cell nowrap muted>{row.category}</Cell>
                  <Cell muted>{row.client}</Cell>
                  <Cell nowrap muted>{row.year}</Cell>
                  <Cell nowrap muted>{row.featured ? "Yes" : "—"}</Cell>
                  <Cell nowrap muted>{row.order}</Cell>
                  <Cell nowrap>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        aria-label={`Edit ${row.title}`}
                        data-testid="admin-edit-project"
                        onClick={() => setEditor({ kind: "projects", item: { ...row, scope: (row.scope || []).join(", ") } })}
                        className="rounded-full p-2 hover:bg-lavender"
                      >
                        <Pencil size={15} strokeWidth={1.7} />
                      </button>
                      <button
                        type="button"
                        aria-label={`Delete ${row.title}`}
                        data-testid="admin-delete-project"
                        onClick={() => remove("projects", row.id)}
                        className="rounded-full p-2 hover:bg-lavender"
                      >
                        <Trash2 size={15} strokeWidth={1.7} />
                      </button>
                    </div>
                  </Cell>
                </tr>
              )}
            />
          )}

          {tab === "partners" && (
            <Table
              testId="admin-partners-table"
              columns={["Name", "Category", "Website", "Order", ""]}
              rows={rows}
              emptyText="No partners yet."
              renderRow={(row) => (
                <tr key={row.id}>
                  <Cell>
                    <span className="font-medium">{row.name}</span>
                    {row.is_placeholder && (
                      <span className="ml-2 text-[10.5px] uppercase" style={{ letterSpacing: "0.12em", color: "#B08A4B" }}>
                        placeholder
                      </span>
                    )}
                  </Cell>
                  <Cell nowrap muted>{row.category}</Cell>
                  <Cell muted>{row.website || "—"}</Cell>
                  <Cell nowrap muted>{row.order}</Cell>
                  <Cell nowrap>
                    <div className="flex gap-2">
                      <button type="button" aria-label={`Edit ${row.name}`} data-testid="admin-edit-partner" onClick={() => setEditor({ kind: "partners", item: { ...row } })} className="rounded-full p-2 hover:bg-lavender">
                        <Pencil size={15} strokeWidth={1.7} />
                      </button>
                      <button type="button" aria-label={`Delete ${row.name}`} data-testid="admin-delete-partner" onClick={() => remove("partners", row.id)} className="rounded-full p-2 hover:bg-lavender">
                        <Trash2 size={15} strokeWidth={1.7} />
                      </button>
                    </div>
                  </Cell>
                </tr>
              )}
            />
          )}

          {tab === "openings" && (
            <Table
              testId="admin-openings-table"
              columns={["Role", "Department", "Location", "Type", "Status", ""]}
              rows={rows}
              emptyText="No openings yet."
              renderRow={(row) => (
                <tr key={row.id}>
                  <Cell>
                    <span className="font-medium">{row.title}</span>
                    {row.is_placeholder && (
                      <span className="ml-2 text-[10.5px] uppercase" style={{ letterSpacing: "0.12em", color: "#B08A4B" }}>
                        placeholder
                      </span>
                    )}
                  </Cell>
                  <Cell nowrap muted>{row.department}</Cell>
                  <Cell nowrap muted>{row.location}</Cell>
                  <Cell nowrap muted>{row.type}</Cell>
                  <Cell nowrap muted>{row.is_open ? "Open" : "Closed"}</Cell>
                  <Cell nowrap>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        aria-label={`Edit ${row.title}`}
                        data-testid="admin-edit-opening"
                        onClick={() => setEditor({ kind: "openings", item: { ...row, responsibilities: (row.responsibilities || []).join("\n") } })}
                        className="rounded-full p-2 hover:bg-lavender"
                      >
                        <Pencil size={15} strokeWidth={1.7} />
                      </button>
                      <button type="button" aria-label={`Delete ${row.title}`} data-testid="admin-delete-opening" onClick={() => remove("openings", row.id)} className="rounded-full p-2 hover:bg-lavender">
                        <Trash2 size={15} strokeWidth={1.7} />
                      </button>
                    </div>
                  </Cell>
                </tr>
              )}
            />
          )}

          {tab === "enquiries" && (
            <Table
              testId="admin-enquiries-table"
              columns={["Received", "Name", "Contact", "Service", "Requirement", ""]}
              rows={rows}
              emptyText="No enquiries yet. Submissions from the contact page appear here."
              renderRow={(row) => (
                <tr key={row.id}>
                  <Cell nowrap muted>{new Date(row.created_at).toLocaleString()}</Cell>
                  <Cell>
                    <span className="font-medium">{row.name}</span>
                    <span className="mt-1 block text-[12px]" style={{ color: "rgba(23,21,34,0.5)" }}>
                      {row.company || "—"}
                    </span>
                  </Cell>
                  <Cell muted>
                    <a href={`mailto:${row.email}`} style={{ color: "#352A59" }}>
                      {row.email}
                    </a>
                    <span className="mt-1 block">{row.phone}</span>
                  </Cell>
                  <Cell nowrap muted>{row.service}</Cell>
                  <Cell muted>
                    <span className="block max-w-[420px]">{row.requirement}</span>
                    {row.message && (
                      <span className="mt-2 block max-w-[420px] text-[12.5px]" style={{ color: "rgba(23,21,34,0.5)" }}>
                        {row.message}
                      </span>
                    )}
                  </Cell>
                  <Cell nowrap>
                    <button type="button" aria-label="Delete enquiry" data-testid="admin-delete-enquiry" onClick={() => remove("enquiries", row.id)} className="rounded-full p-2 hover:bg-lavender">
                      <Trash2 size={15} strokeWidth={1.7} />
                    </button>
                  </Cell>
                </tr>
              )}
            />
          )}

          {tab === "applications" && (
            <Table
              testId="admin-applications-table"
              columns={["Received", "Candidate", "Contact", "Area", "Applying For", "Portfolio", ""]}
              rows={rows}
              emptyText="No applications yet."
              renderRow={(row) => (
                <tr key={row.id}>
                  <Cell nowrap muted>{new Date(row.created_at).toLocaleString()}</Cell>
                  <Cell>
                    <span className="font-medium">{row.name}</span>
                    {row.message && (
                      <span className="mt-1 block max-w-[320px] text-[12.5px]" style={{ color: "rgba(23,21,34,0.5)" }}>
                        {row.message}
                      </span>
                    )}
                  </Cell>
                  <Cell muted>
                    <a href={`mailto:${row.email}`} style={{ color: "#352A59" }}>
                      {row.email}
                    </a>
                    <span className="mt-1 block">{row.phone}</span>
                  </Cell>
                  <Cell nowrap muted>{row.area}</Cell>
                  <Cell nowrap muted>{row.opening || "—"}</Cell>
                  <Cell muted>{row.portfolio || "—"}</Cell>
                  <Cell nowrap>
                    <button type="button" aria-label="Delete application" onClick={() => remove("applications", row.id)} className="rounded-full p-2 hover:bg-lavender">
                      <Trash2 size={15} strokeWidth={1.7} />
                    </button>
                  </Cell>
                </tr>
              )}
            />
          )}

          {tab === "campaigns" && (
            <Table
              testId="admin-campaigns-table"
              columns={["Received", "Cause", "Focus Area", "Contact", "Story", ""]}
              rows={rows}
              emptyText="No campaign submissions yet."
              renderRow={(row) => (
                <tr key={row.id}>
                  <Cell nowrap muted>{new Date(row.created_at).toLocaleString()}</Cell>
                  <Cell>
                    <span className="font-medium">{row.cause_title}</span>
                    <span className="mt-1 block text-[12px]" style={{ color: "rgba(23,21,34,0.5)" }}>
                      {row.organisation || "—"} · {row.location || "—"}
                    </span>
                  </Cell>
                  <Cell nowrap muted>{row.focus_area}</Cell>
                  <Cell muted>
                    <span className="block">{row.contact_name}</span>
                    <a href={`mailto:${row.email}`} style={{ color: "#352A59" }}>
                      {row.email}
                    </a>
                    <span className="mt-1 block">{row.phone}</span>
                  </Cell>
                  <Cell muted>
                    <span className="block max-w-[380px]">{row.story}</span>
                  </Cell>
                  <Cell nowrap>
                    <button type="button" aria-label="Delete campaign" onClick={() => remove("campaigns", row.id)} className="rounded-full p-2 hover:bg-lavender">
                      <Trash2 size={15} strokeWidth={1.7} />
                    </button>
                  </Cell>
                </tr>
              )}
            />
          )}

          {tab === "partner-requests" && (
            <Table
              testId="admin-partner-requests-table"
              columns={["Received", "Organisation", "Contact", "Type", "Message", ""]}
              rows={rows}
              emptyText="No partnership requests yet."
              renderRow={(row) => (
                <tr key={row.id}>
                  <Cell nowrap muted>{new Date(row.created_at).toLocaleString()}</Cell>
                  <Cell>
                    <span className="font-medium">{row.organisation}</span>
                  </Cell>
                  <Cell muted>
                    <span className="block">{row.contact_name}</span>
                    <a href={`mailto:${row.email}`} style={{ color: "#352A59" }}>
                      {row.email}
                    </a>
                    <span className="mt-1 block">{row.phone || "—"}</span>
                  </Cell>
                  <Cell nowrap muted>{row.partnership_type}</Cell>
                  <Cell muted>
                    <span className="block max-w-[380px]">{row.message || "—"}</span>
                  </Cell>
                  <Cell nowrap>
                    <button type="button" aria-label="Delete request" onClick={() => remove("partner-requests", row.id)} className="rounded-full p-2 hover:bg-lavender">
                      <Trash2 size={15} strokeWidth={1.7} />
                    </button>
                  </Cell>
                </tr>
              )}
            />
          )}
        </div>
      </main>

      {editor?.kind === "projects" && (
        <ProjectEditor item={editor.item} onClose={() => setEditor(null)} onSave={saveProject} />
      )}
      {editor?.kind === "partners" && (
        <PartnerEditor item={editor.item} onClose={() => setEditor(null)} onSave={savePartner} />
      )}
      {editor?.kind === "openings" && (
        <OpeningEditor item={editor.item} onClose={() => setEditor(null)} onSave={saveOpening} />
      )}
    </div>
  );
}

function ProjectEditor({ item, onClose, onSave }) {
  const [form, setForm] = useState(item);
  const set = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));
  return (
    <Panel title={form.id ? "Edit Project" : "Add Project"} onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSave(form);
        }}
        data-testid="admin-project-form"
      >
        <div className="grid gap-5 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Label>Title *</Label>
            <Input value={form.title} onChange={set("title")} required data-testid="admin-project-title" />
          </div>
          <div>
            <Label>Category *</Label>
            <Select value={form.category} onChange={set("category")} options={CATEGORIES} data-testid="admin-project-category" />
          </div>
          <div>
            <Label>Year</Label>
            <Input value={form.year} onChange={set("year")} />
          </div>
          <div>
            <Label>Client / Partner</Label>
            <Input value={form.client} onChange={set("client")} />
          </div>
          <div>
            <Label>Order</Label>
            <Input type="number" value={form.order} onChange={set("order")} />
          </div>
          <div className="sm:col-span-2">
            <Label>Image URL</Label>
            <Input value={form.image} onChange={set("image")} placeholder="https://…" />
          </div>
          <div className="sm:col-span-2">
            <Label>Summary</Label>
            <Area value={form.summary} onChange={set("summary")} />
          </div>
          <div className="sm:col-span-2">
            <Label>Description</Label>
            <Area value={form.description} onChange={set("description")} />
          </div>
          <div className="sm:col-span-2">
            <Label>Scope of work (comma separated)</Label>
            <Input value={form.scope} onChange={set("scope")} placeholder="Concept, Production, Delivery" />
          </div>
          <label className="flex items-center gap-3 text-[13.5px]">
            <input type="checkbox" checked={!!form.featured} onChange={(e) => setForm((p) => ({ ...p, featured: e.target.checked }))} data-testid="admin-project-featured" />
            Show on homepage (featured)
          </label>
          <label className="flex items-center gap-3 text-[13.5px]">
            <input type="checkbox" checked={!!form.is_placeholder} onChange={(e) => setForm((p) => ({ ...p, is_placeholder: e.target.checked }))} />
            Mark as placeholder
          </label>
        </div>
        <div className="mt-8 flex gap-3">
          <button type="submit" className="btn btn-primary" data-testid="admin-project-save">
            Save Project
          </button>
          <button type="button" onClick={onClose} className="btn btn-outline">
            Cancel
          </button>
        </div>
      </form>
    </Panel>
  );
}

function PartnerEditor({ item, onClose, onSave }) {
  const [form, setForm] = useState(item);
  const set = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));
  return (
    <Panel title={form.id ? "Edit Partner" : "Add Partner"} onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSave(form);
        }}
        data-testid="admin-partner-form"
      >
        <div className="grid gap-5 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Label>Name *</Label>
            <Input value={form.name} onChange={set("name")} required data-testid="admin-partner-name" />
          </div>
          <div>
            <Label>Category *</Label>
            <Select value={form.category} onChange={set("category")} options={PARTNER_CATEGORY_NAMES} data-testid="admin-partner-category" />
          </div>
          <div>
            <Label>Order</Label>
            <Input type="number" value={form.order} onChange={set("order")} />
          </div>
          <div className="sm:col-span-2">
            <Label>Logo URL</Label>
            <Input value={form.logo} onChange={set("logo")} placeholder="https://…" />
          </div>
          <div className="sm:col-span-2">
            <Label>Website</Label>
            <Input value={form.website} onChange={set("website")} placeholder="https://…" />
          </div>
          <label className="flex items-center gap-3 text-[13.5px]">
            <input type="checkbox" checked={!!form.is_placeholder} onChange={(e) => setForm((p) => ({ ...p, is_placeholder: e.target.checked }))} />
            Mark as placeholder
          </label>
        </div>
        <div className="mt-8 flex gap-3">
          <button type="submit" className="btn btn-primary" data-testid="admin-partner-save">
            Save Partner
          </button>
          <button type="button" onClick={onClose} className="btn btn-outline">
            Cancel
          </button>
        </div>
      </form>
    </Panel>
  );
}

function OpeningEditor({ item, onClose, onSave }) {
  const [form, setForm] = useState(item);
  const set = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));
  return (
    <Panel title={form.id ? "Edit Opening" : "Add Opening"} onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSave(form);
        }}
        data-testid="admin-opening-form"
      >
        <div className="grid gap-5 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Label>Role title *</Label>
            <Input value={form.title} onChange={set("title")} required data-testid="admin-opening-title" />
          </div>
          <div>
            <Label>Department *</Label>
            <Select value={form.department} onChange={set("department")} options={CAREERS.opportunities} data-testid="admin-opening-department" />
          </div>
          <div>
            <Label>Location</Label>
            <Input value={form.location} onChange={set("location")} />
          </div>
          <div>
            <Label>Type</Label>
            <Select value={form.type} onChange={set("type")} options={["Full-time", "Part-time", "Contract", "Internship", "Freelance"]} />
          </div>
          <div>
            <Label>Experience</Label>
            <Input value={form.experience} onChange={set("experience")} placeholder="2–5 years" />
          </div>
          <div className="sm:col-span-2">
            <Label>Description</Label>
            <Area value={form.description} onChange={set("description")} />
          </div>
          <div className="sm:col-span-2">
            <Label>Responsibilities (one per line)</Label>
            <Area value={form.responsibilities} onChange={set("responsibilities")} />
          </div>
          <div>
            <Label>Order</Label>
            <Input type="number" value={form.order} onChange={set("order")} />
          </div>
          <div className="flex flex-col gap-3 pt-7">
            <label className="flex items-center gap-3 text-[13.5px]">
              <input type="checkbox" checked={!!form.is_open} onChange={(e) => setForm((p) => ({ ...p, is_open: e.target.checked }))} data-testid="admin-opening-isopen" />
              Open for applications
            </label>
            <label className="flex items-center gap-3 text-[13.5px]">
              <input type="checkbox" checked={!!form.is_placeholder} onChange={(e) => setForm((p) => ({ ...p, is_placeholder: e.target.checked }))} />
              Mark as placeholder
            </label>
          </div>
        </div>
        <div className="mt-8 flex gap-3">
          <button type="submit" className="btn btn-primary" data-testid="admin-opening-save">
            Save Opening
          </button>
          <button type="button" onClick={onClose} className="btn btn-outline">
            Cancel
          </button>
        </div>
      </form>
    </Panel>
  );
}
